'use client';

import { interpolateStringWithData } from 'utils';
import { Textarea } from '../../atoms/Textarea';
import { FeedbackButton } from '../../molecules/FeedbackButton/FeedbackButton';
import { FormModal } from '../../molecules/FormModal/FormModal';
import {
  MAX_FEEDBACK_CHARS,
  feedbackFormSchema,
  type FeedbackModalData,
  type FeedbackModalProps,
} from './FeedbackModal.types';

import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';

export const FeedbackModal = ({
  microcopy,
  onSubmit,
  className = '',
}: FeedbackModalProps) => {
  const {
    control,
    handleSubmit,
    formState: { isValid, errors },
    watch,
  } = useForm<FeedbackModalData>({
    resolver: zodResolver(feedbackFormSchema),
    mode: 'onChange',
    defaultValues: { feedback: '' },
  });

  const watchedFeedback = watch('feedback');

  const onFormSubmit = (data: FeedbackModalData) => {
    onSubmit(data);
  };

  const triggerElement = (
    <FeedbackButton
      microcopy={{
        cta: microcopy.title,
        buttonAriaLabel: microcopy.feedbackButton.buttonAriaLabel,
        closeAriaLabel: microcopy.feedbackButton.closeAriaLabel,
      }}
      vertical
    />
  );

  const modalContent = (
    <div className="flex h-full flex-col">
      {microcopy.subtitle ? (
        <p className="mb-3 text-md text-grey-700">{microcopy.subtitle}</p>
      ) : null}
      <div className="flex flex-1 flex-col">
        <Controller
          control={control}
          name="feedback"
          render={({ field }) => (
            <Textarea
              data-testid={microcopy.inputTextareaTestId}
              placeholder={microcopy.placeholder}
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
              maxLength={MAX_FEEDBACK_CHARS}
              className="h-full min-h-0 w-full flex-1 resize-none overflow-auto"
              aria-invalid={Boolean(errors.feedback)}
            />
          )}
        />
      </div>
      <div className="">
        <p className="text-xs text-grey-500">
          {interpolateStringWithData(microcopy.remaining, {
            characters: Math.max(
              0,
              MAX_FEEDBACK_CHARS - watchedFeedback.length
            ).toString(),
          })}
        </p>
      </div>
    </div>
  );

  return (
    <FormModal
      size="small"
      trigger={triggerElement}
      content={modalContent}
      title={microcopy.title}
      close={microcopy.close}
      submit={microcopy.primaryCta}
      disabled={!isValid}
      cancel={microcopy.secondaryCta}
      footer={{
        title: microcopy.supportTitle,
        description: microcopy.supportDescription,
      }}
      onSubmit={handleSubmit(onFormSubmit)}
      className={className}
    />
  );
};
